import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { Button, Form } from "react-bootstrap";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../hooks/useTypedSelector";
import { resetPassword } from "../../infrastructure/slices/authSlice/authSlice";
import { ForgotPasswordResponseModel } from "../../domain/models/userModel";
import { useState } from "react";
import { toast } from "sonner";

export const ResetPasswordForm = () => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get("token");

  const [resetSuccess, setResetSuccess] = useState<boolean | null>();
  const [resetError, setResetError] = useState<string>("");

  const form = useForm({
    defaultValues: {
      password: "",
      passwordCheck: "",
    },
    onSubmit: async (values) => {
      await dispatch(
        resetPassword({
          token: token as string,
          password: values.value.password,
        }),
      ).then((result) => {
        if (
          (result.payload as ForgotPasswordResponseModel).status === "success"
        ) {
          setResetSuccess(true);
          toast.success(t("user.password.reset.success"));
          navigate("/auth/login?resetSuccess=true");
        } else {
          setResetError(
            (result.payload as ForgotPasswordResponseModel).message,
          );
          setResetSuccess(false);
        }
      });
    },
    validatorAdapter: zodValidator(),
  });

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <h3 className="text-center mb-4">{t("Reset password")}</h3>
        <form.Field
          name="password"
          validators={{
            onSubmit: z
              .string()
              .min(8, t("Password must be at least 8 characters")),
          }}
        >
          {(field) => {
            return (
              <Form.Group className="mb-3">
                <Form.Label>{t("Password")}</Form.Label>
                <Form.Control
                  id={field.name}
                  name={field.name}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  isInvalid={field.state.meta.errors.length > 0}
                />
                {field.state.meta.errors ? (
                  <Form.Control.Feedback type="invalid">
                    {field.state.meta.errors.join(", ")}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>
            );
          }}
        </form.Field>

        <form.Field
          name="passwordCheck"
          validators={{
            onSubmit: z.string().refine((data) => {
              return data === form.getFieldValue("password");
            }, t("Passwords must match")),
          }}
        >
          {(field) => {
            return (
              <Form.Group className="mb-3">
                <Form.Label>{t("Password confirm")}</Form.Label>
                <Form.Control
                  id={field.name}
                  name={field.name}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  isInvalid={field.state.meta.errors.length > 0}
                />
                {field.state.meta.errors ? (
                  <Form.Control.Feedback type="invalid">
                    {field.state.meta.errors.join(", ")}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>
            );
          }}
        </form.Field>
        <form.Subscribe>
          <Button type="submit" className="mt-3 w-100">
            {t("Submit")}
          </Button>
        </form.Subscribe>
        {resetSuccess === true && (
          <h6 style={{ textAlign: "center" }}>
            {t("user.password.reset.success")}
          </h6>
        )}
        {resetSuccess === false && (
          <h6 style={{ textAlign: "center" }}>
            {t("user.password.reset.error", { error: resetError })}
          </h6>
        )}
      </form>
    </>
  );
};
